// agents.js — single source of truth for the human-readable labels we
// show next to AI-generated answers.
//
// Why this file exists:
// The "Ask your restaurant" box (Insights tab) attributes its answer to
// an agent ("Analyst"). Ship 8 will introduce a small roster of named
// agents and may rename or specialise them. Keeping the label in ONE
// place means that swap is a one-line change here instead of a string
// hunt across the edge function + every UI surface that renders it.
//
// The edge function (supabase/functions/ask-restaurant/index.ts) returns
// agent_label = 'Analyst' on every response; the UI prefers the value
// from the response but falls back to ANALYST_LABEL so the two never
// drift out of sync.
//
// IMPORTANT: keep ANALYST_LABEL byte-for-byte 'Analyst'. AskAiModal.jsx
// imports it and the ask-restaurant edge fn returns agent_label='Analyst';
// the two MUST stay in sync.
export const ANALYST_LABEL = 'Analyst'

// Ship 8 — agent persona labels.
//
// We frame ARM's AI surfaces as a small roster of named back-office
// specialists rather than anonymous "AI brief" cards. Every persona
// string lives here so a rename is a one-line change instead of a
// string hunt across the UI.
//
// Persona family note: the Ask-AI 'Analyst' chip (ANALYST_LABEL above)
// and the Insights weekly "analyst" copy (AGENT_LABELS.weeklyInsights)
// are the SAME persona family — the analyst who reads your numbers.
// Keep their voice consistent if either ever changes.
//
// Sentence case; NO emoji/icons in any of these labels.
export const AGENT_LABELS = {
  // #1 — Daily Huddle hero brief eyebrow (charcoal card).
  huddleBrief: 'Morning brief from your AI manager',
  // #2 — Group roll-up brief (charcoal card).
  groupRollup: 'Group manager brief',
  // #3/#4 — Weekly Insights header (single card; the analyst persona).
  weeklyInsights: 'What your analyst saw this week',
  // #5 — Single-unit menu engineering AI read eyebrow.
  strategistRead: 'Strategist read',
  // #6 — Cross-unit menu engineering AI read eyebrow.
  groupStrategistRead: 'Multi-unit strategist read',
  // #7 — Cross-unit comparison brief eyebrow.
  groupComparison: 'Operations analyst comparison',
  // #8 — Drafted PO email sheet (review-and-send framing).
  reorderEmailDraft: 'Reorder agent drafted this email — review and send',
  // #9 — Cross-unit consolidated reorder brief eyebrow.
  groupReorderBrief: "This week's reorder agent brief",
  // #10 — Marketing drafts inbox header. BASE label only; the component
  // appends " — N drafts ready" with the live count.
  marketingDrafts: 'Marketing agent',
  // #11 — Daily Huddle marketing/social banner eyebrow.
  marketingActivity: 'Marketing agent activity',
  // #12 — Catering quote modal title.
  cateringQuoteDraft: 'Catering agent quote draft',
  // #13 — Group catering pipeline header.
  groupCateringPipeline: 'Catering agent — group pipeline',
  // #14 — Schedule suggestions section eyebrow.
  scheduleSuggestions: 'Schedule agent suggestions',
}

// Shared muted badge text rendered next to AI surfaces that carry a
// generated_at timestamp ("Agent ran · 2 hr ago"). See AgentRanBadge.jsx.
export const AGENT_RAN_LABEL = 'Agent ran'
